// MedicalEditor.jsx
import React, { useState, useEffect } from 'react';
import { Form, Row, Col, Button, Alert } from 'react-bootstrap';
import DropdownField from '../../DropDownField/DropDownField';
import RequiredField from '../../../SharedComponents/RequiredField/RequiredField';
import { BLOOD_TYPES } from '../../../../Constants/constants';
import { REQUIRED_FIELDS } from '../../MainForm/config/formConfig';
import { useTranslation } from 'react-i18next';

const MedicalEditor = ({ currentUser, onSave, onCancel, initialData, formErrors }) => {
    const { t } = useTranslation('medical');

    const [medical, setMedical] = useState({
        blood_type: '',
        medication: '',
        allergy: '',
        id: null,
        user_id: currentUser?.id
    });

    const [touched, setTouched] = useState({});

    useEffect(() => {
        if (initialData) {
            setMedical({
                ...initialData,
                user_id: currentUser?.id
            });
        }
    }, [initialData, currentUser]);

    const handleFieldChange = (field, value) => {
        setMedical(prev => ({
            ...prev,
            [field]: value
        }));
        setTouched(prev => ({
            ...prev,
            [field]: true
        }));
    };

    const getFieldLabel = (fieldName) => {
        const weight = REQUIRED_FIELDS.medical[fieldName];
        const isRequired = weight === 1.0;
        return (
            <span>
                {t(`fields.${fieldName}.label`)}
                {isRequired && <span style={{ color: 'red' }}> *</span>}
            </span>
        );
    };

    const bloodTypeOptions = BLOOD_TYPES.map(type => ({
        value: type,
        label: type
    }));

    const getFieldStyle = (fieldName) => {
        const weight = REQUIRED_FIELDS.medical[fieldName];
        const isRequired = weight === 1.0;
        const isEmpty = !medical[fieldName] || medical[fieldName].toString().trim() === '';

        if (isRequired && isEmpty) {
            return {
                border: '2px solid red',
                borderRadius: '4px',
                outline: 'none',
                boxShadow: 'none'
            };
        }
        return {};
    };

    const shouldShowError = (fieldName) => {
        return touched[fieldName] && formErrors[`0_${fieldName}`];
    };

    const canSave = () => {
        return Object.entries(REQUIRED_FIELDS.medical).every(([field, weight]) => {
            if (weight === 1.0) {
                const value = medical[field];
                return value && value.toString().trim() !== '';
            }
            return true;
        });
    };

    const handleSave = () => {
        const allTouched = {};
        Object.keys(REQUIRED_FIELDS.medical).forEach(field => {
            allTouched[field] = true;
        });
        setTouched(allTouched);

        onSave([medical]);
    };

    return (
        <Form>
            <Row className="mb-3">
                <Col md={4}>
                    <DropdownField
                        controlId="blood_type"
                        label={getFieldLabel('blood_type')}
                        name="blood_type"
                        value={medical.blood_type}
                        options={bloodTypeOptions}
                        handleChange={(e) => handleFieldChange('blood_type', e.target.value)}
                        error={shouldShowError('blood_type') ? formErrors['0_blood_type'] : null}
                        isRequired={true}
                        style={getFieldStyle('blood_type')}
                    />
                </Col>
                <Col md={4}>
                    <RequiredField
                        label={getFieldLabel('medication')}
                        name="medication"
                        value={medical.medication}
                        onChange={(e) => handleFieldChange('medication', e.target.value)}
                        placeholder={t('fields.medication.placeholder')}
                        error={shouldShowError('medication') ? formErrors['0_medication'] : null}
                        style={getFieldStyle('medication')}
                    />
                </Col>
                <Col md={4}>
                    <RequiredField
                        label={getFieldLabel('allergy')}
                        name="allergy"
                        value={medical.allergy}
                        onChange={(e) => handleFieldChange('allergy', e.target.value)}
                        placeholder={t('fields.allergy.placeholder')}
                        error={shouldShowError('allergy') ? formErrors['0_allergy'] : null}
                        style={getFieldStyle('allergy')}
                    />
                </Col>
            </Row>

            {Object.entries(formErrors).map(([field, error]) => {
                const baseField = field.split('_')[1];
                return shouldShowError(baseField) && (
                    <Alert key={field} variant="danger" className="mt-2 p-2">
                        {error}
                    </Alert>
                );
            })}

            <div className="d-flex justify-content-end">
                <Button
                    variant="success"
                    onClick={handleSave}
                    className="me-2"
                    disabled={!canSave()}
                    style={{
                        opacity: canSave() ? 1 : 0.6,
                        cursor: canSave() ? 'pointer' : 'not-allowed'
                    }}
                >
                    {t('buttons.save')}
                </Button>
                <Button
                    variant="secondary"
                    onClick={onCancel}
                >
                    {t('buttons.cancel')}
                </Button>
            </div>
        </Form>
    );
};

export default MedicalEditor;